import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Spinner,
} from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { DocumentFactsEditor } from './DocumentFactsEditor';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { useToast } from '../../../shared/context/ToastContext';

/**
 * Allowed file types for supporting documents
 */
const ALLOWED_TYPES = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/plain': 'TXT',
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * Format file size for display
 */
function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file icon based on content type
 */
function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) {
    return (
      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (contentType === 'application/pdf') {
    return (
      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  return (
    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

/**
 * Status badge for document upload/processing status
 */
function StatusBadge({ status }) {
  switch (status) {
    case 'processed':
      return <Badge variant="green" size="sm">Processed</Badge>;
    case 'processing':
      return (
        <div className="flex items-center gap-1">
          <Spinner size="sm" />
          <Badge variant="blue" size="sm">Processing</Badge>
        </div>
      );
    case 'uploaded':
      return <Badge variant="gray" size="sm">Uploaded</Badge>;
    case 'sheets_split':
    case 'classifying':
      return (
        <div className="flex items-center gap-1">
          <Spinner size="sm" />
          <Badge variant="blue" size="sm">Processing</Badge>
        </div>
      );
    case 'pending':
      return <Badge variant="yellow" size="sm">Pending</Badge>;
    case 'failed':
      return <Badge variant="red" size="sm">Failed</Badge>;
    default:
      return <Badge variant="gray" size="sm">{status || 'Unknown'}</Badge>;
  }
}

/**
 * Single document row in the panel
 */
function DocumentRow({
  document,
  estimateId,
  editable,
  onDelete,
  onDownload,
  documentFacts,
}) {
  const [factsExpanded, setFactsExpanded] = useState(false);

  const isProcessing = document.uploadStatus === 'processing';
  const isProcessed = document.uploadStatus === 'processed';
  const hasFacts = documentFacts?.divisions?.length > 0;
  const canExpand = isProcessed || hasFacts;

  const handleRowClick = () => {
    if (canExpand) setFactsExpanded((prev) => !prev);
  };

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
      {/* Document Header Row */}
      <div
        className={`flex items-center gap-3 px-4 py-3 ${canExpand ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''}`}
        onClick={handleRowClick}
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
        onKeyDown={(e) => {
          if (canExpand && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleRowClick();
          }
        }}
      >
        {/* File Icon */}
        <div className="flex-shrink-0">
          {getFileIcon(document.contentType)}
        </div>

        {/* File Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {document.originalFileName || document.fileName}
            </span>
            {document.fileSizeBytes && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({formatFileSize(document.fileSizeBytes)})
              </span>
            )}
            {document.totalPages != null && document.totalPages > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                · {document.totalPages} page{document.totalPages !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {document.createdAt && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Uploaded {new Date(document.createdAt).toLocaleDateString()}
            </p>
          )}
          {document.totalPages != null
            && document.pagesProcessed != null
            && document.pagesProcessed < document.totalPages && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              Processed {document.pagesProcessed} of {document.totalPages} pages — estimate page budget reached
            </p>
          )}
        </div>

        {/* Status */}
        <StatusBadge status={document.uploadStatus} />

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Expand facts toggle */}
          {canExpand && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFactsExpanded(!factsExpanded);
              }}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title={factsExpanded ? 'Hide AI analysis' : 'Show AI analysis'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${factsExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}

          {/* Download button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(document);
            }}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
            title="Download document"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>

          {/* Delete button (edit mode only) */}
          {editable && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(document);
              }}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
              title="Delete document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Processing placeholder */}
      {isProcessing && factsExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-6 text-center">
          <Spinner size="md" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Extracting facts from document...</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">This may take a few minutes</p>
        </div>
      )}

      {/* AI analysis drawer (collapsed by default) */}
      {factsExpanded && !isProcessing && (
        <div className="border-t border-gray-200 dark:border-gray-800">
          <DocumentFactsEditor divisions={documentFacts?.divisions} />
        </div>
      )}
    </div>
  );
}

/**
 * Upload progress row for a document being uploaded
 */
function UploadingDocumentRow({ doc }) {
  return (
    <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-900">
      <div className="flex-shrink-0">
        {getFileIcon(doc.file.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {doc.file.name}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({formatFileSize(doc.file.size)})
          </span>
        </div>
        {doc.status === 'uploading' && (
          <div className="mt-1">
            <div className="flex items-center gap-2">
              <Spinner size="sm" />
              <span className="text-xs text-gray-600 dark:text-gray-300">Uploading...</span>
            </div>
            <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${doc.progress}%` }}
              />
            </div>
          </div>
        )}
        {doc.status === 'error' && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{doc.error || 'Upload failed'}</p>
        )}
      </div>
      {doc.status === 'uploading' && (
        <Badge variant="blue" size="sm">Uploading</Badge>
      )}
      {doc.status === 'error' && (
        <Badge variant="red" size="sm">Error</Badge>
      )}
    </div>
  );
}

/**
 * EstimateDocumentsPanel - Shows documents for an estimate with view/edit capabilities
 *
 * View mode: List documents, download, expand to see facts (read-only)
 * Edit mode: All of view mode + upload, delete, edit facts inline
 */
export function EstimateDocumentsPanel({
  estimateId,
  editable = false,
  title = 'Supporting Documents',
  onProcessingStatusChange,
}) {
  const { showError, showSuccess } = useToast();

  // Document list state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Facts state — only the per-document AI analysis is rendered, no editing.
  const [documentFacts, setDocumentFacts] = useState({});

  // Upload state (edit mode)
  const [uploadingDocs, setUploadingDocs] = useState([]);
  const fileInputRef = useRef(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Processing poll state
  const pollIntervalRef = useRef(null);
  const initialLoadDone = useRef(false);

  // Page budget state — drives whether the Add Documents button is visible
  const [pageBudget, setPageBudget] = useState(null);

  const refreshPageBudget = useCallback(async () => {
    if (!estimateId) return;
    try {
      const result = await estimatesApi.getEstimatePageBudget(estimateId);
      setPageBudget(result);
    } catch (err) {
      // Non-critical — fall back to allowing uploads if the meter can't load.
      console.warn('Failed to fetch page budget:', err);
    }
  }, [estimateId]);

  // Fetch documents on mount
  useEffect(() => {
    if (!estimateId) return;
    fetchDocuments();
    refreshPageBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  // Derive whether any documents are processing (stable boolean, won't cause effect churn)
  const hasProcessing = documents.some(
    (d) => d.uploadStatus === 'processing' ||
           d.uploadStatus === 'sheets_split' ||
           d.uploadStatus === 'classifying'
  );

  // Emit processing status changes to parent wizard so it can gate the
  // "Proceed to AI Estimation" button until uploads + extraction finish.
  const processingCount = documents.filter(
    (d) => d.uploadStatus === 'processing' ||
           d.uploadStatus === 'sheets_split' ||
           d.uploadStatus === 'classifying' ||
           d.uploadStatus === 'pending',
  ).length;
  const failedCount = documents.filter((d) => d.uploadStatus === 'failed').length;
  const processedCount = documents.filter((d) => d.uploadStatus === 'processed').length;
  const uploadingCount = uploadingDocs.filter((d) => d.status === 'uploading').length;

  useEffect(() => {
    if (typeof onProcessingStatusChange !== 'function') return;
    onProcessingStatusChange({
      total: documents.length,
      processed: processedCount,
      processing: processingCount,
      failed: failedCount,
      uploading: uploadingCount,
    });
  }, [
    documents.length,
    processedCount,
    processingCount,
    failedCount,
    uploadingCount,
    onProcessingStatusChange,
  ]);

  // Poll for processing documents — keyed on boolean flag, not the documents array
  useEffect(() => {
    if (!hasProcessing) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    if (!pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await estimatesApi.getDocumentsProcessingStatus(estimateId);
          if (status.processing === 0) {
            // All done — stop polling, then refetch full document list
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            await fetchDocuments();
            refreshPageBudget();
          }
        } catch (err) {
          // Silently retry on next poll
        }
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProcessing, estimateId]);

  const fetchDocuments = async () => {
    try {
      // Only show full loading spinner on initial load
      if (!initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const result = await estimatesApi.listEstimateDocuments(estimateId);
      const docs = (result.documents || []).filter((d) => !d.isSourceFile);
      setDocuments(docs);

      // Fetch facts for processed documents
      const factsMap = {};
      for (const doc of docs) {
        if (doc.uploadStatus === 'processed' && doc.documentId) {
          try {
            const facts = await estimatesApi.getDocumentFacts(estimateId, doc.documentId);
            factsMap[doc.documentId] = facts;
          } catch (err) {
            factsMap[doc.documentId] = { divisions: [] };
          }
        }
      }
      setDocumentFacts(factsMap);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  // Download handler
  const handleDownload = useCallback(async (document) => {
    try {
      const { downloadUrl } = await estimatesApi.getDocumentDownloadUrl(
        estimateId,
        document.documentId
      );
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
      showError('Failed to download document');
    }
  }, [estimateId, showError]);

  // Delete handlers
  const handleDeleteClick = useCallback((document) => {
    setDeleteTarget(document);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await estimatesApi.deleteDocument(estimateId, deleteTarget.documentId);
      setDocuments((prev) => prev.filter((d) => d.documentId !== deleteTarget.documentId));
      setDocumentFacts((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.documentId];
        return next;
      });
      showSuccess('Document deleted');
      refreshPageBudget();
    } catch (err) {
      console.error('Failed to delete document:', err);
      showError('Failed to delete document');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, estimateId, refreshPageBudget, showError, showSuccess]);

  // Upload handlers
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    const errors = [];
    const newDocs = [];

    for (const file of files) {
      if (!ALLOWED_TYPES[file.type]) {
        errors.push(`${file.name}: File type not supported`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size exceeds 100MB limit`);
        continue;
      }
      newDocs.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        status: 'uploading',
        progress: 0,
        error: null,
      });
    }

    if (errors.length > 0) {
      showError(errors.join('; '));
    }

    if (newDocs.length > 0) {
      setUploadingDocs((prev) => [...prev, ...newDocs]);
      // Start uploading each file
      for (const doc of newDocs) {
        uploadSingleDocument(doc);
      }
    }

    // Reset input
    e.target.value = '';
  }, [showError]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadSingleDocument = async (doc) => {
    try {
      const result = await estimatesApi.uploadSupportingDocument(
        estimateId,
        doc.file,
        (progress) => {
          setUploadingDocs((prev) =>
            prev.map((d) => (d.id === doc.id ? { ...d, progress } : d))
          );
        }
      );

      // Upload complete — remove from uploading list, add to documents as processing
      setUploadingDocs((prev) => prev.filter((d) => d.id !== doc.id));
      setDocuments((prev) => [
        ...prev,
        {
          documentId: result.documentId,
          fileName: result.fileName || doc.file.name,
          originalFileName: doc.file.name,
          contentType: doc.file.type,
          fileSizeBytes: doc.file.size,
          uploadStatus: 'processing',
          createdAt: new Date().toISOString(),
        },
      ]);

      // Kick off processing automatically — no separate Process button.
      // Best-effort: the endpoint is idempotent over already-allocated docs,
      // so multiple in-flight uploads each calling it is harmless.
      try {
        await estimatesApi.startDocumentProcessing(estimateId);
      } catch (err) {
        showError(err.response?.data?.error || 'Failed to start document processing');
      }
      refreshPageBudget();
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadingDocs((prev) =>
        prev.map((d) =>
          d.id === doc.id ? { ...d, status: 'error', error: err.message || 'Upload failed' } : d
        )
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
            <span className="ml-3 text-gray-500 dark:text-gray-400 text-sm">Loading documents...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasDocuments = documents.length > 0 || uploadingDocs.length > 0;
  const budgetExhausted = pageBudget != null && pageBudget.pagesRemaining <= 0;

  const truncatedDocs = documents.filter(
    (d) => d.totalPages != null
      && d.pagesProcessed != null
      && d.pagesProcessed < d.totalPages,
  );
  const skippedPages = truncatedDocs.reduce(
    (sum, d) => sum + (d.totalPages - d.pagesProcessed),
    0,
  );

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{title}</CardTitle>
            <div className="flex items-center gap-3">
              {documents.length > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {documents.length} document{documents.length !== 1 ? 's' : ''}
                </span>
              )}
              {editable && !budgetExhausted && (
                <label className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Documents
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={Object.keys(ALLOWED_TYPES).join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {truncatedDocs.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-200 text-sm">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
                </svg>
                <div>
                  <p className="font-medium">
                    {skippedPages} page{skippedPages !== 1 ? 's' : ''} skipped across {truncatedDocs.length} document{truncatedDocs.length !== 1 ? 's' : ''} — estimate page budget reached
                  </p>
                  <p className="text-xs mt-1 text-amber-700 dark:text-amber-300">
                    Only the allocated pages were analyzed. Upgrade your plan to raise the per-estimate page limit.
                  </p>
                </div>
              </div>
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
              <button
                onClick={fetchDocuments}
                className="ml-2 text-red-800 dark:text-red-300 underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          {hasDocuments ? (
            <div className="space-y-3">
              {/* Uploading documents */}
              {uploadingDocs.map((doc) => (
                <UploadingDocumentRow key={doc.id} doc={doc} />
              ))}

              {/* Existing documents */}
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.documentId}
                  document={doc}
                  estimateId={estimateId}
                  editable={editable}
                  onDelete={handleDeleteClick}
                  onDownload={handleDownload}
                  documentFacts={documentFacts[doc.documentId]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">No supporting documents</p>
              {editable && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Upload blueprints, plans, or other project documents
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteTarget?.originalFileName || deleteTarget?.fileName}"? This will also remove any extracted facts. This action cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

export default EstimateDocumentsPanel;
